import React, { useState } from 'react';
import axios from 'axios';
import { PauseCircle, CalendarClock, Undo2, PlayCircle, FileText, FileDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const STATUS_META = {
  on_hold: { label: 'On Hold', cls: 'bg-orange-100 text-orange-700' },
  under_review: { label: 'Under Review', cls: 'bg-sky-100 text-sky-700' },
  reverted: { label: 'Reverted', cls: 'bg-rose-100 text-rose-700' },
};

const ACTIONS = {
  hold: { title: 'Put promotion on hold', btn: 'Put on hold', note: 'The change is paused. If it is already in effect, the previous position and salary are put back until you release it.' },
  review: { title: 'Hold promotion for review', btn: 'Hold for review', note: 'Paused like a hold. HR is emailed on the review date to release or revert it.' },
  revert: { title: 'Revert promotion', btn: 'Revert promotion', note: 'The previous position and salary are put back permanently. A reversal letter becomes available to download.' },
};

const ActionDialog = ({ action, onClose, onSubmit, busy }) => {
  const [reason, setReason] = useState('');
  const [reviewDate, setReviewDate] = useState('');
  const meta = ACTIONS[action];
  return (
    <Dialog open={!!action} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="promotion-action-dialog">
        <DialogHeader><DialogTitle>{meta?.title}</DialogTitle></DialogHeader>
        <p className="text-xs text-gray-500">{meta?.note}</p>
        <div>
          <Label>Reason *</Label>
          <Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} data-testid="promotion-action-reason" />
        </div>
        {action === 'review' && (
          <div>
            <Label>Review date *</Label>
            <Input type="date" value={reviewDate} onChange={e => setReviewDate(e.target.value)} data-testid="promotion-action-review-date" />
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !reason.trim() || (action === 'review' && !reviewDate)}
            className={action === 'revert' ? 'bg-rose-600 hover:bg-rose-700' : ''}
            onClick={() => onSubmit({ action, reason, review_date: reviewDate || null })} data-testid="promotion-action-submit">
            {meta?.btn}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const PromotionStatusActions = ({ employeeId, promo, index, isViewer, onChanged, onDownload }) => {
  const [action, setAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const status = promo.status || 'active';

  const submit = async (body) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/promotion-actions/${employeeId}/${promo.id}`, body);
      const label = { hold: 'Promotion on hold', review: 'Held for review', revert: 'Promotion reverted', resume: 'Promotion released' }[body.action];
      toast({ title: label, description: r.data.employee_changed ? `Position is now ${r.data.position}.` : 'The employee\'s current position and salary were not changed.' });
      setAction(null);
      onChanged?.();
    } catch (e) {
      toast({ title: 'Action failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const small = 'h-7 px-2 text-[11px]';
  return (
    <div className="mt-2 space-y-1.5">
      {status !== 'active' && (
        <p className="text-[11px] text-gray-600" data-testid={`promotion-status-note-${index}`}>
          <b>{STATUS_META[status]?.label}</b>{promo.review_date ? ` until ${promo.review_date}` : ''}
          {promo.status_reason ? ` — ${promo.status_reason}` : ''}
          {promo.status_by ? <span className="text-gray-400"> · by {promo.status_by}</span> : null}
        </p>
      )}
      {!isViewer && status !== 'reverted' && (
        <div className="flex flex-wrap gap-1.5">
          {status === 'active' ? (
            <>
              <Button type="button" variant="outline" size="sm" className={`${small} border-orange-300 text-orange-700`} onClick={() => setAction('hold')} data-testid={`promotion-hold-${index}`}>
                <PauseCircle className="h-3.5 w-3.5 mr-1" /> Hold
              </Button>
              <Button type="button" variant="outline" size="sm" className={`${small} border-sky-300 text-sky-700`} onClick={() => setAction('review')} data-testid={`promotion-review-${index}`}>
                <CalendarClock className="h-3.5 w-3.5 mr-1" /> Hold for review
              </Button>
            </>
          ) : (
            <Button type="button" variant="outline" size="sm" className={`${small} border-green-300 text-green-700`} disabled={busy}
              onClick={() => window.confirm('Release this promotion? The new position and salary will apply (or wait for the effective date).') && submit({ action: 'resume' })}
              data-testid={`promotion-resume-${index}`}>
              <PlayCircle className="h-3.5 w-3.5 mr-1" /> Release
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" className={`${small} border-rose-300 text-rose-700`} onClick={() => setAction('revert')} data-testid={`promotion-revert-${index}`}>
            <Undo2 className="h-3.5 w-3.5 mr-1" /> Revert
          </Button>
        </div>
      )}
      {status === 'reverted' && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-gray-500">Reversal letter:</span>
          <Button type="button" variant="outline" size="sm" className={`${small} border-rose-300 text-rose-700`} onClick={() => onDownload(promo, 'pdf', 'revert')} data-testid={`promotion-revert-pdf-${index}`}>
            <FileText className="h-3.5 w-3.5 mr-1" /> PDF
          </Button>
          <Button type="button" variant="outline" size="sm" className={`${small} border-rose-300 text-rose-700`} onClick={() => onDownload(promo, 'docx', 'revert')} data-testid={`promotion-revert-word-${index}`}>
            <FileDown className="h-3.5 w-3.5 mr-1" /> Word
          </Button>
        </div>
      )}
      {action && <ActionDialog action={action} busy={busy} onClose={() => setAction(null)} onSubmit={submit} />}
    </div>
  );
};
